import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDenseTableLabels } from '../hooks/useDenseTableLabels';
import { useTranslation } from 'react-i18next';
import type { TDocumentDefinitions } from 'pdfmake/interfaces';
import { useProfileContext } from '../hooks/useProfileContext';
import {
  apiGetFeesSummary, apiGetMemberInvoiceData, apiGetTours,
  type FeesSummaryRow, type MemberInvoice, type FeesFilter, type TourDTO,
} from '../api';
import { generatePdfBlobViaServer } from '../lib/pdf';
import { reportSaveError } from '../lib/saveStatus';
import { can } from '../lib/permissions';
import PageHeader from '../components/PageHeader';

/**
 * PayoutsPage (/band/:bandSlug/payouts) — the tour manager's recap of musician
 * fees ("cachets") per member over a period / tour. Shows what each person is
 * owed and has been paid, and generates a per-member recap invoice (PDF).
 *
 * Amounts are NEVER converted between currencies: totals that would mix
 * currencies are shown alongside the per-currency breakdown, never instead.
 *
 * FINANCE GATE: this is financial data — the page (and its NavLink) is only
 * reachable with the `finance.fees` capability (chantier C, 2026-09-25); the
 * API enforces the same rule too (403).
 */
export default function PayoutsPage() {
  const denseRef = useRef<HTMLDivElement | null>(null);
  useDenseTableLabels(denseRef);
  const { t } = useTranslation();
  const { activeProfileId, profiles } = useProfileContext();
  const activeBand = activeProfileId ? profiles.find((p) => p.id === activeProfileId) : null;
  const financeAccess = can(activeBand, 'finance.fees');

  const [rows, setRows] = useState<FeesSummaryRow[]>([]);
  const [tours, setTours] = useState<TourDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Period / tour filter (all optional).
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [tourId, setTourId] = useState('');
  // Which member's invoice is being generated right now.
  const [generating, setGenerating] = useState<string | null>(null);

  const filter: FeesFilter = useMemo(() => ({
    from: from || undefined,
    to: to || undefined,
    tour_id: tourId || undefined,
  }), [from, to, tourId]);

  const load = useCallback(() => {
    if (!activeProfileId || !financeAccess) return;
    setLoading(true);
    setError(null);
    apiGetFeesSummary(activeProfileId, filter)
      .then(setRows)
      .catch((e) => { setError(e?.message || 'Failed to load payouts'); reportSaveError('finance', e); })
      .finally(() => setLoading(false));
  }, [activeProfileId, financeAccess, filter]);

  useEffect(() => { load(); }, [load]);

  // Tours of the active band, for the optional tour filter.
  useEffect(() => {
    if (!activeProfileId) { setTours([]); return; }
    let cancelled = false;
    apiGetTours()
      .then((data) => {
        if (cancelled) return;
        setTours((data || []).filter((tr) => !activeProfileId || String(tr.band_id ?? '') === activeProfileId));
      })
      .catch(() => { if (!cancelled) setTours([]); });
    return () => { cancelled = true; };
  }, [activeProfileId]);

  // Build the pdfmake docDef for one member's recap invoice.
  const buildInvoiceDoc = (inv: MemberInvoice): TDocumentDefinitions => {
    const periodLabel = inv.period.from || inv.period.to
      ? t('payouts.periodRange', { from: inv.period.from || '…', to: inv.period.to || '…' })
      : t('payouts.allPeriods');
    const tourLabel = inv.period.tour_id
      ? (tours.find((tr) => String(tr.id) === inv.period.tour_id)?.name || '')
      : '';
    const lines = inv.lines.map((l) => [
      { text: (l.date || '').slice(0, 10), fontSize: 9 },
      { text: l.show_name || '—', fontSize: 9 },
      { text: [l.venue_name, l.city].filter(Boolean).join(', ') || '—', fontSize: 9 },
      { text: `${(l.amount ?? 0).toLocaleString()} ${l.currency}`, alignment: 'right' as const, fontSize: 9 },
      { text: t(`fees.status.${l.status}`), fontSize: 9 },
    ]);
    const totals = inv.totals.map((tot) => [
      { text: tot.currency, bold: true, fontSize: 9 },
      { text: `${tot.due.toLocaleString()} ${tot.currency}`, alignment: 'right' as const, fontSize: 9 },
      { text: `${tot.paid.toLocaleString()} ${tot.currency}`, alignment: 'right' as const, fontSize: 9 },
      { text: String(tot.gigs), alignment: 'right' as const, fontSize: 9 },
    ]);
    return {
      content: [
        { text: (inv.band.name || '').toUpperCase(), alignment: 'center', bold: true, fontSize: 16 },
        { text: t('payouts.invoiceTitle'), alignment: 'center', fontSize: 13, margin: [0, 4, 0, 2] },
        { text: inv.member.name || '', alignment: 'center', bold: true, fontSize: 12 },
        { text: [inv.member.role, inv.member.category].filter(Boolean).join(' · '), alignment: 'center', fontSize: 10, color: '#666' },
        { text: [periodLabel, tourLabel].filter(Boolean).join('  ·  '), alignment: 'center', fontSize: 10, color: '#666', margin: [0, 2, 0, 12] },
        inv.lines.length === 0
          ? { text: t('payouts.noLines'), color: '#888', italics: true }
          : {
            table: {
              headerRows: 1,
              widths: [56, '*', '*', 80, 60],
              body: [
                [
                  { text: t('payouts.colDate'), bold: true, fontSize: 9 },
                  { text: t('payouts.colShow'), bold: true, fontSize: 9 },
                  { text: t('payouts.colVenue'), bold: true, fontSize: 9 },
                  { text: t('payouts.colAmount'), bold: true, fontSize: 9, alignment: 'right' },
                  { text: t('payouts.colStatus'), bold: true, fontSize: 9 },
                ],
                ...lines,
              ],
            },
            layout: { hLineColor: () => '#ddd', vLineColor: () => '#eee', paddingTop: () => 4, paddingBottom: () => 4 },
          },
        ...(inv.totals.length
          ? [
            { text: t('payouts.totalsHeading'), bold: true, margin: [0, 14, 0, 4] },
            {
              table: {
                headerRows: 1,
                widths: [60, '*', '*', 50],
                body: [
                  [
                    { text: t('payouts.colCurrency'), bold: true, fontSize: 9 },
                    { text: t('payouts.colDue'), bold: true, fontSize: 9, alignment: 'right' },
                    { text: t('payouts.colPaid'), bold: true, fontSize: 9, alignment: 'right' },
                    { text: t('payouts.colGigs'), bold: true, fontSize: 9, alignment: 'right' },
                  ],
                  ...totals,
                ],
              },
              layout: { hLineColor: () => '#ddd', vLineColor: () => '#eee', paddingTop: () => 4, paddingBottom: () => 4 },
            },
          ]
          : []),
        { text: inv.note || t('payouts.notConverted'), margin: [0, 14, 0, 0], fontSize: 9, color: '#888', italics: true },
      ],
      defaultStyle: { font: 'Roboto', fontSize: 10 },
      pageSize: 'A4',
      pageMargins: [40, 50, 40, 50],
      // Serializable spec — the server rebuilds the pdfmake pagination
      // function and substitutes {page}/{pages} (see server/routes/pdf.js).
      footerSpec: {
        text: '{page} / {pages}', alignment: 'right', margin: [0, 10, 40, 0], fontSize: 9, color: '#888',
      },
    };
  };

  const generateInvoice = async (memberId: string, memberName: string) => {
    if (!activeProfileId) return;
    setGenerating(memberId);
    try {
      const inv = await apiGetMemberInvoiceData(activeProfileId, memberId, filter);
      const url = await generatePdfBlobViaServer(buildInvoiceDoc(inv));
      // Download via an anchor (same pattern as the setlist / checklist export).
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(memberName || 'member').replace(/[^\w-]+/g, '_')}-invoice.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e: any) {
      setError(e?.message || t('payouts.invoiceFailed'));
      reportSaveError('finance', e);
    } finally {
      setGenerating(null);
    }
  };

  if (!activeProfileId) {
    return <div className="page"><div className="loading">{t('payouts.selectBandFirst')}</div></div>;
  }
  if (!financeAccess) {
    return <div className="page"><div className="card"><p>{t('payouts.noAccess')}</p></div></div>;
  }

  return (
    <div ref={denseRef} className="page" data-density="socle">
      <PageHeader
        title={t('payouts.title')}
        subtitle={activeBand ? t('payouts.forBand', { name: activeBand.name }) : ''}
      />

      {/* Period + tour filter (optional — empty = every show). */}
      <div className="card" style={{ maxWidth: 900, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ width: 150 }}>
            <label>{t('payouts.from')}</label>
            <input className="form-input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div style={{ width: 150 }}>
            <label>{t('payouts.to')}</label>
            <input className="form-input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div style={{ minWidth: 180, flex: 1 }}>
            <label>{t('payouts.tour')}</label>
            <select className="form-input" value={tourId} onChange={(e) => setTourId(e.target.value)}>
              <option value="">{t('payouts.allTours')}</option>
              {tours.map((tr) => <option key={tr.id} value={String(tr.id)}>{tr.name}</option>)}
            </select>
          </div>
          <button className="btn btn-secondary" onClick={() => { setFrom(''); setTo(''); setTourId(''); }}>
            {t('payouts.clear')}
          </button>
        </div>
      </div>

      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
      {loading && <p className="text-dim">{t('payouts.loading')}</p>}
      {!loading && rows.length === 0 && <p className="text-dim">{t('payouts.empty')}</p>}

      {!loading && rows.length > 0 && (
        <div className="table-wrapper" style={{ maxWidth: 1000 }}>
          <table className="tbl-dense">
            <thead>
              <tr>
                <th>{t('payouts.colMember')}</th>
                <th data-num>{t('payouts.colTotalDue')}</th>
                <th data-num>{t('payouts.colTotalPaid')}</th>
                <th data-num>{t('payouts.colGigs')}</th>
                <th>{t('payouts.colByCurrency')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.team_member_id}>
                  <td style={{ fontWeight: 600 }}>{r.member_name}</td>
                  {/* Raw cross-currency sums — a hint flags that they mix
                      currencies; the by_currency breakdown is authoritative. */}
                  <td title={t('payouts.mixedHint')} data-num>{r.total_due.toLocaleString()}</td>
                  <td title={t('payouts.mixedHint')} data-num>{r.total_paid.toLocaleString()}</td>
                  <td data-num>{r.gigs}</td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: '0.85rem' }}>
                      {r.by_currency.map((c) => (
                        <span key={c.currency} className="text-dim">
                          {t('payouts.currencyLine', {
                            currency: c.currency,
                            due: c.due.toLocaleString(),
                            paid: c.paid.toLocaleString(),
                          })}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      className="btn btn-secondary btn-small"
                      disabled={generating === r.team_member_id}
                      onClick={() => generateInvoice(r.team_member_id, r.member_name)}
                    >
                      {generating === r.team_member_id ? '…' : t('payouts.generateInvoice')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
